import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { OptionI, ValidationObj } from '../../../types/interface';
import validationUtils from '../../utils/validationUtils';
import { capitalizeFirstLetter } from 'utils/helpers';
import { t } from 'i18next';
import './StandardSelect.scss';
import ReactSelect from 'components/base/ReactSelect';
import { StateManagerProps } from 'react-select/dist/declarations/src/useStateManager';

interface StandardSelectPropsI extends StateManagerProps {
  id: string;
  placeholder: string;
  label: string;
  formClassName: string;
  showError: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange: (value: any) => void;
  validations: ValidationObj[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  compareValue?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  disabled?: boolean;
  options: OptionI[];
  isMulti?: boolean;
  formGroupClassName?: string;
}

const StandardSelect = (props: StandardSelectPropsI) => {
  const [dirty, setDirty] = React.useState(false);
  const [errorKey, setErrorKey] = React.useState<string>('');
  const [errorText, setErrorText] = React.useState<string>('');
  const [valid, setValid] = React.useState(true);
  const [selectedValues, setSelectedValues] = React.useState<OptionI[]>([]);

  useEffect(() => {
    checkValidity(props.value);
  }, [props.compareValue, props.validations, dirty, errorText, props.value]);

  const onBlur = () => {
    setDirty(true);
  };

  const renderError = () => {
    if (!valid && (dirty || props.showError)) {
      return (
        <Form.Text className="text-danger error standard-error-text">
          {errorKey
            ? capitalizeFirstLetter(t(errorKey))
            : capitalizeFirstLetter(errorText)}
        </Form.Text>
      );
    }
    return null;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onChange = (e: any) => {
    checkValidity(e);
    if (e) {
      let value;
      if (props.isMulti) {
        value = e.map((option: OptionI) => option.value);
      } else {
        value = e.value;
      }
      props.onChange(value);
    } else {
      props.onChange([]);
    }
  };

  const checkValidity = (value: string | number) => {
    let isValid = true;
    let errorText = '';
    let errorKey: string | undefined = '';
    for (const validation of props.validations || []) {
      if (validation.type == 'CompareValue') {
        if (
          !validation.disabled &&
          !validationUtils[validation.type](value, props.compareValue)
        ) {
          errorText = validation.message;
          isValid = false;
          break;
        }
      } else if (validation.type !== 'checkFormValidity') {
        if (
          !validation.disabled &&
          !validationUtils[validation.type](value, validation)
        ) {
          errorText = validation.message;
          errorKey = validation.key;
          isValid = false;
          break;
        }
      }
    }
    setValid(isValid);
    setErrorText(errorText);
    setErrorKey(errorKey);
  };

  const generateClassname = () => {
    return `fs-9 basic-multi-select ${props.formClassName} ${
      props.disabled ? 'disabled' : ''
    }`;
  };

  useEffect(() => {
    if (props.isMulti) {
      setSelectedValues(
        props?.options?.filter(
          (option: OptionI) => props?.value?.includes(option.value)
        )
      );
    } else {
      setSelectedValues(
        props?.options?.filter(
          (option: OptionI) => option.value === props.value
        )
      );
    }
  }, [props.value, props.options]);

  return (
    <Form.Group
      className={`text-start ${props.formGroupClassName}`}
      id="standard-input"
    >
      {props.label && (
        <div className="fs-14">{capitalizeFirstLetter(props.label)}</div>
      )}
      <div className="form-icon-container">
        <ReactSelect
          id={props.id}
          className={generateClassname()}
          placeholder={capitalizeFirstLetter(props.placeholder)}
          onBlur={onBlur}
          isDisabled={props.disabled}
          options={props.options}
          onChange={onChange}
          value={selectedValues}
          isMulti={!!props.isMulti}
          isClearable={props.isClearable}
          blurInputOnSelect
        />
        {renderError()}
      </div>
    </Form.Group>
  );
};

export default StandardSelect;
